/**
 * Fixed-timestep game loop (accumulator pattern). Simulation ticks at a fixed
 * rate regardless of display refresh; render interpolates. Game logic must
 * never read wall-clock time or frame delta directly.
 */
export const TICK_RATE = 60
export const TICK_MS = 1000 / TICK_RATE
export const TICK_S = 1 / TICK_RATE

const MAX_FRAME_MS = 250 // clamp huge tab-switch deltas; avoids spiral of death

export class GameLoop {
  private acc = 0
  private last = 0
  private rafId = 0
  private running = false

  /** Rolling render-FPS estimate for the dev overlay. */
  fps = 0

  /**
   * OVERRIDE slow-motion: scales how much sim time each real frame feeds the
   * accumulator. Tick dt and tick math stay identical — determinism per seed
   * is untouched; only the real-time pacing of ticks changes.
   */
  timeScale = 1

  constructor(
    private update: (dt: number) => void,
    private render: (alpha: number) => void
  ) {}

  start(): void {
    if (this.running) return
    this.running = true
    this.last = performance.now()
    this.acc = 0
    const frame = (now: number) => {
      if (!this.running) return
      let delta = now - this.last
      this.last = now
      if (delta > MAX_FRAME_MS) delta = MAX_FRAME_MS
      if (delta > 0) this.fps = this.fps * 0.95 + (1000 / delta) * 0.05
      this.acc += delta * this.timeScale
      while (this.acc >= TICK_MS) {
        this.update(TICK_S)
        this.acc -= TICK_MS
      }
      this.render(this.acc / TICK_MS)
      this.rafId = requestAnimationFrame(frame)
    }
    this.rafId = requestAnimationFrame(frame)
  }

  stop(): void {
    this.running = false
    cancelAnimationFrame(this.rafId)
  }
}
